import type { ApolloClient } from "@apollo/client";
import { useApolloClient } from "@apollo/client/react";
import { useEffect, useState } from "react";

import {
  GetMapRecordGeoItemsDocument,
  type GetMapRecordGeoItemsQuery,
  type GetMapRecordGeoItemsQueryVariables,
} from "~/features/entities/gqls";
import { requireMapGeoRecords } from "~/features/entities/utils/mapHelpers";

const RECORD_IDS_PER_REQUEST = 100;
const MAX_CONCURRENT_REQUESTS = 2;

type MapGeoResponse = GetMapRecordGeoItemsQuery["cnutGetRecordGeoItemsByRecordIds"];
type MapGeoRecord = Extract<MapGeoResponse, { __typename?: "RecordGeoItemsByRecordIdsOutput" }>["records"][number];

export async function fetchMapGeoRecords(
  client: Pick<ApolloClient, "query">,
  tenantId: string,
  recordIds: string[],
  signal?: AbortSignal,
): Promise<MapGeoRecord[]> {
  const chunks: string[][] = [];
  for (let offset = 0; offset < recordIds.length; offset += RECORD_IDS_PER_REQUEST) {
    chunks.push(recordIds.slice(offset, offset + RECORD_IDS_PER_REQUEST));
  }

  const records: MapGeoRecord[] = [];
  for (let offset = 0; offset < chunks.length; offset += MAX_CONCURRENT_REQUESTS) {
    signal?.throwIfAborted();
    const responses = await Promise.all(
      chunks.slice(offset, offset + MAX_CONCURRENT_REQUESTS).map((chunk) =>
        client.query<GetMapRecordGeoItemsQuery, GetMapRecordGeoItemsQueryVariables>({
          query: GetMapRecordGeoItemsDocument,
          variables: { tenantId, recordIds: chunk },
          fetchPolicy: "no-cache",
          context: { queryDeduplication: false, fetchOptions: { signal } },
        }),
      ),
    );
    for (const response of responses) {
      if (!response.data) throw new Error("Map GeoItem response is empty");
      records.push(...requireMapGeoRecords(response.data.cnutGetRecordGeoItemsByRecordIds));
    }
  }
  return records;
}

type MapGeoRecordsState = {
  tenantId: string;
  recordIds: string[] | null;
  records: MapGeoRecord[];
  error: Error | null;
};

export function useMapGeoRecords(tenantId: string, recordIds: string[]) {
  const client = useApolloClient();
  const [state, setState] = useState<MapGeoRecordsState>({ tenantId: "", recordIds: null, records: [], error: null });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    void fetchMapGeoRecords(client, tenantId, recordIds, controller.signal).then(
      (records) => {
        if (active) setState({ tenantId, recordIds, records, error: null });
      },
      (error: unknown) => {
        if (active)
          setState({
            tenantId,
            recordIds,
            records: [],
            error: error instanceof Error ? error : new Error(String(error)),
          });
      },
    );
    return () => {
      active = false;
      controller.abort();
    };
  }, [client, recordIds, tenantId]);

  if (state.tenantId !== tenantId || state.recordIds !== recordIds) return { records: [], loading: true };
  if (state.error) throw state.error;
  return { records: state.records, loading: false };
}
